import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';
import { Watch, CheckCircle2, Users } from '@/utils/icons';
import corosLogo from '@/assets/logos/coros-logo.png';

interface UserInfo {
    username: string;
    role?: string;
    full_name?: string;
    first_name?: string;
}

interface TerraWidgetResponse {
    url: string;
    reference_id: string;
}

/**
 * Wearable device option
 */
interface WearableOption {
    id: string;
    name: string;
    description: string;
    provider: 'whoop' | 'terra' | 'catapult' | 'hawkin';
    icon?: string;
    available: boolean;
    logoPath?: {
        dark: string;
        light: string;
    };
}

/**
 * Available wearable devices
 */
const WEARABLE_OPTIONS: WearableOption[] = [
    {
        id: 'whoop',
        name: 'Whoop',
        description: 'Connect your Whoop device to track HRV, recovery, and sleep',
        provider: 'whoop',
        available: true,
        logoPath: {
            dark: '/logos/whoop/Logo/Circle/WHOOP Circle White.svg',
            light: '/logos/whoop/Logo/Circle/WHOOP Circle Black.svg',
        },
    },
    {
        id: 'garmin',
        name: 'Garmin',
        description: 'Connect your Garmin device',
        provider: 'terra',
        available: true,
        logoPath: {
            dark: '/logos/garmin/garmin-branding/Garmin_Connect_app_1024x1024-02.png',
            light: '/logos/garmin/garmin-branding/Garmin_Connect_app_1024x1024-02.png',
        },
    },
    {
        id: 'coros',
        name: 'COROS',
        description: 'Connect your COROS watch',
        provider: 'terra',
        available: true,
        logoPath: {
            dark: corosLogo,
            light: corosLogo,
        },
    },
    {
        id: 'catapult',
        name: 'Catapult Vector',
        description: 'Connect your Catapult OpenField account',
        provider: 'catapult',
        available: true,
        logoPath: {
            dark: '/logos/catapult/catapult-icon-white.webp',
            light: '/logos/catapult/catapult-icon-black.webp',
        },
    },
    {
        id: 'hawkin',
        name: 'Hawkin Dynamics',
        description: 'Connect your Hawkin force plates',
        provider: 'hawkin',
        available: true,
        logoPath: {
            dark: '/logos/hawkin/hawkin-icon-white.webp',
            light: '/logos/hawkin/hawkin-icon-black.webp',
        },
    },
    {
        id: 'vald',
        name: 'Vald',
        description: 'Connect your Vald device',
        provider: 'terra',
        available: false,
        logoPath: {
            dark: '/logos/vald/vald-logo.png',
            light: '/logos/vald/vald-logo.png',
        },
    },
    {
        id: 'oura',
        name: 'Oura',
        description: 'Connect your Oura ring',
        provider: 'terra',
        available: true,
        logoPath: {
            dark: '/logos/oura/dark-bg.png',
            light: '/logos/oura/light-bg.png',
        },
    },
    {
        id: 'suunto',
        name: 'Suunto',
        description: 'Connect your Suunto watch',
        provider: 'terra',
        available: true,
        logoPath: {
            dark: '/logos/suunto/suunto-logo.png',
            light: '/logos/suunto/suunto-logo.png',
        },
    },
];

/**
 * ConnectWearable component that shows a list of available wearable devices.
 * Users can select Whoop (direct OAuth) or any Terra device (opens Terra widget).
 */
interface ConnectionStatus {
    whoopConnected: boolean;
    garminConnected: boolean;
    corosConnected: boolean;
    ouraConnected: boolean;
    suuntoConnected: boolean;
}

const ConnectWearable: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
        whoopConnected: false,
        garminConnected: false,
        corosConnected: false,
        ouraConnected: false,
        suuntoConnected: false,
    });
    const [reconnectPending, setReconnectPending] = useState<WearableOption | null>(null);
    const infoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const popupRef = useRef<Window | null>(null);
    const currentProviderRef = useRef<string | null>(null);

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }

        // Check connection status
        checkConnectionStatus();

        // Cleanup timeout on unmount
        return () => {
            if (infoTimeoutRef.current) {
                clearTimeout(infoTimeoutRef.current);
            }
        };
    }, [navigate]);

    // Detect when user returns to this tab after OAuth flow
    // (handles case where browser opens Terra widget as a tab instead of a popup)
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (
                document.visibilityState === 'visible' &&
                popupRef.current &&
                !popupRef.current.closed
            ) {
                // User returned to this tab while Terra popup/tab exists
                const provider = currentProviderRef.current;
                popupRef.current = null;
                currentProviderRef.current = null;
                setLoading(false);

                if (!provider) return;

                // Wait for the auth_success webhook to be processed
                await new Promise(r => setTimeout(r, 2000));

                // Refresh connection status and check if connected
                const status = await checkConnectionStatus();
                const isProviderConnected = (provider === 'garmin' && status.garminConnected) ||
                    (provider === 'coros' && status.corosConnected) ||
                    (provider === 'oura' && status.ouraConnected) ||
                    (provider === 'suunto' && status.suuntoConnected);
                if (isProviderConnected) {
                    const providerName = provider === 'coros' ? 'COROS' : provider === 'suunto' ? 'Suunto' : provider.charAt(0).toUpperCase() + provider.slice(1);
                    setInfo(
                        `${providerName} connected! Your historical data is being synced — this may take several minutes before your activities appear.`
                    );
                    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
                    infoTimeoutRef.current = setTimeout(() => setInfo(''), 15000);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const checkConnectionStatus = async (): Promise<ConnectionStatus> => {
        const defaultStatus = { whoopConnected: false, garminConnected: false, corosConnected: false, ouraConnected: false, suuntoConnected: false };
        try {
            const token = localStorage.getItem('access');
            if (!token) return defaultStatus;

            // Check Whoop connection status
            const whoopResponse = await fetch(getApiUrl('/api/users/whoopstatus/'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const whoopResult = await safeJsonParse<{ connected: boolean }>(whoopResponse, 'checking Whoop status');

            // Check Garmin connection status
            const garminResponse = await fetch(getApiUrl('/api/terra/connection-status/?provider=garmin'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const garminResult = await safeJsonParse<{ connected: boolean }>(garminResponse, 'checking Garmin status');

            // Check COROS connection status
            const corosResponse = await fetch(getApiUrl('/api/terra/connection-status/?provider=coros'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const corosResult = await safeJsonParse<{ connected: boolean }>(corosResponse, 'checking COROS status');

            // Check Oura connection status
            const ouraResponse = await fetch(getApiUrl('/api/terra/connection-status/?provider=oura'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const ouraResult = await safeJsonParse<{ connected: boolean }>(ouraResponse, 'checking Oura status');

            // Check Suunto connection status
            const suuntoResponse = await fetch(getApiUrl('/api/terra/connection-status/?provider=suunto'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const suuntoResult = await safeJsonParse<{ connected: boolean }>(suuntoResponse, 'checking Suunto status');

            const status = {
                whoopConnected: whoopResult.success && whoopResult.data?.connected === true,
                garminConnected: garminResult.success && garminResult.data?.connected === true,
                corosConnected: corosResult.success && corosResult.data?.connected === true,
                ouraConnected: ouraResult.success && ouraResult.data?.connected === true,
                suuntoConnected: suuntoResult.success && suuntoResult.data?.connected === true,
            };
            setConnectionStatus(status);
            return status;
        } catch (error) {
            console.error('Error checking connection status:', error);
            return defaultStatus;
        }
    };

    const isConnected = (wearableId: string): boolean => {
        if (wearableId === 'whoop') {
            return connectionStatus.whoopConnected;
        } else if (wearableId === 'garmin') {
            return connectionStatus.garminConnected;
        } else if (wearableId === 'coros') {
            return connectionStatus.corosConnected;
        } else if (wearableId === 'oura') {
            return connectionStatus.ouraConnected;
        } else if (wearableId === 'suunto') {
            return connectionStatus.suuntoConnected;
        }
        return false;
    };

    const openTerraWidget = async (provider?: string) => {
        try {
            setLoading(true);
            setError('');
            if (infoTimeoutRef.current) {
                clearTimeout(infoTimeoutRef.current);
                infoTimeoutRef.current = null;
            }
            setInfo('');
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const requestBody: { lang: string; provider?: string } = { lang: 'en' };
            if (provider) {
                requestBody.provider = provider;
            }

            // Open popup FIRST (synchronously in the click handler) so mobile
            // browsers don't block it. We navigate to the widget URL after the
            // API call returns. This preserves the user-gesture call stack.
            const width = 600;
            const height = 700;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;

            const popup = window.open(
                'about:blank',
                'TerraWidget',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
            );

            if (!popup) {
                setError('Popup blocked. Please allow popups for this site.');
                setLoading(false);
                return;
            }

            const response = await fetch(getApiUrl('/api/terra/generate-widget-session/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                popup.close();
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate widget session');
            }

            const data: TerraWidgetResponse = await response.json();

            // Navigate the already-open popup to the widget URL
            popup.location.href = data.url;

            // Store refs so the visibility-change handler can detect "navigate back" scenarios
            popupRef.current = popup;
            currentProviderRef.current = provider || null;

            // Capture connection status before widget opens
            const wasConnected = provider === 'garmin' ? connectionStatus.garminConnected
                : provider === 'coros' ? connectionStatus.corosConnected
                : provider === 'oura' ? connectionStatus.ouraConnected
                : provider === 'suunto' ? connectionStatus.suuntoConnected
                : false;
            const providerName = provider ? (provider === 'coros' ? 'COROS' : provider === 'suunto' ? 'Suunto' : provider.charAt(0).toUpperCase() + provider.slice(1)) : 'Device';

            // Helper: check connection status with a short delay to let the backend
            // process the auth_success webhook before we query.
            const checkAndShowSuccess = async () => {
                const tokenCheck = localStorage.getItem('access');
                if (!tokenCheck || !provider) return;

                // Small delay for the auth_success S3→SQS pipeline to create the WearableDevice
                await new Promise(r => setTimeout(r, 2000));

                const providerResponse = await fetch(getApiUrl(`/api/terra/connection-status/?provider=${provider}`), {
                    headers: {
                        'Authorization': `Bearer ${tokenCheck}`,
                        'Content-Type': 'application/json',
                    },
                });
                const providerResult = await safeJsonParse<{ connected: boolean }>(providerResponse, `checking ${providerName} status`);
                const isNowConnected = providerResult.success && providerResult.data?.connected === true;

                const statusKey = `${provider}Connected` as keyof ConnectionStatus;
                setConnectionStatus(prev => ({ ...prev, [statusKey]: isNowConnected }));

                if (isNowConnected && !wasConnected) {
                    setInfo(
                        `${providerName} connected! Your historical data is being synced — this may take several minutes before your activities appear.`
                    );
                    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
                    infoTimeoutRef.current = setTimeout(() => setInfo(''), 15000);
                }
            };

            // Listen for popup/tab to close
            const checkClosed = setInterval(async () => {
                // Guard: if popupRef was already cleared by the visibility handler, skip
                if (!popupRef.current) {
                    clearInterval(checkClosed);
                    return;
                }
                if (popup.closed) {
                    clearInterval(checkClosed);
                    setLoading(false);
                    popupRef.current = null;
                    currentProviderRef.current = null;

                    try {
                        if (provider === 'garmin' || provider === 'coros' || provider === 'oura' || provider === 'suunto') {
                            await checkAndShowSuccess();
                        } else {
                            await checkConnectionStatus();
                        }
                    } catch (error) {
                        console.error('Error checking connection status after widget close:', error);
                    }
                }
            }, 500);
            
        } catch (err: any) {
            // Close orphaned popup on network error or JSON parse failure
            if (popupRef.current && !popupRef.current.closed) {
                popupRef.current.close();
            }
            setError(err.message || 'Failed to generate widget session');
            setLoading(false);
        }
    };

    const handleWearableSelect = async (wearable: WearableOption) => {
        if (!wearable.available) {
            return; // Don't do anything for unavailable wearables
        }
        
        if (wearable.provider === 'whoop') {
            navigate('/connect-whoop');
        } else if (wearable.provider === 'catapult') {
            navigate('/connect-catapult-vector');
        } else if (wearable.provider === 'hawkin') {
            navigate('/connect-hawkin');
        } else if (wearable.id === 'garmin' || wearable.id === 'coros' || wearable.id === 'oura' || wearable.id === 'suunto') {
            // If already connected, show confirmation before reconnecting
            if (isConnected(wearable.id)) {
                setReconnectPending(wearable);
                return;
            }
            // For Terra providers, open Terra widget directly to provider login (skips provider selection)
            await openTerraWidget(wearable.id);
        } else {
            // For other Terra providers, open widget showing all providers
            await openTerraWidget();
        }
    };

    /**
     * Get the logo path for a wearable based on theme
     */
    const getWearableLogo = (wearable: WearableOption): string | null => {
        if (!wearable.logoPath) {
            return null;
        }
        return isDark ? wearable.logoPath.dark : wearable.logoPath.light;
    };

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">

            <div className="max-w-4xl w-full space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Your Wearable Device
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Select your wearable device to connect and start tracking your health and fitness data.
                    </p>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                        {error}
                    </div>
                )}

                {/* Info Display */}
                {info && (
                    <div className="cgm-alert-info px-4 py-3 rounded-md mb-4 text-sm">
                        {info}
                    </div>
                )}

                {/* Reconnect Confirmation */}
                {reconnectPending && (
                    <div className="cgm-alert-info px-4 py-4 rounded-md mb-4 text-sm">
                        <p className="mb-3">
                            <strong>{reconnectPending.name}</strong> is already connected. Reconnecting will reset your sync &mdash; historical data may take several minutes to reappear.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={async () => {
                                    const provider = reconnectPending.id;
                                    setReconnectPending(null);
                                    await openTerraWidget(provider);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Continue with Reconnect
                            </button>
                            <button
                                onClick={() => setReconnectPending(null)}
                                className="px-4 py-2 cgm-text-secondary text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Personal Devices */}
                <div className="mt-8" data-tour="wearable-personal-devices">
                    <h3 className="cgm-text-primary text-xl font-semibold mb-4">Personal Devices</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {WEARABLE_OPTIONS.filter(w => w.available && w.provider !== 'catapult' && w.provider !== 'hawkin').map((wearable) => {
                            const logoPath = getWearableLogo(wearable);
                            return (
                                <button
                                    key={wearable.id}
                                    onClick={() => handleWearableSelect(wearable)}
                                    disabled={loading && wearable.provider === 'terra'}
                                    data-tour={wearable.id === 'whoop' ? 'wearable-whoop-card' : undefined}
                                    className={`cgm-card p-6 text-left hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] cursor-pointer relative border-2 border-transparent hover:border-opacity-20 ${
                                        loading && wearable.provider === 'terra' ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {isConnected(wearable.id) && (
                                        <div className="absolute top-4 right-4 z-10">
                                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                                        </div>
                                    )}
                                    <div className="flex items-center space-x-5">
                                        <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                                            {logoPath ? (
                                                <img
                                                    src={logoPath}
                                                    alt={`${wearable.name} logo`}
                                                    className="w-full h-full object-contain"
                                                />
                                            ) : (
                                                <Watch className="w-10 h-10 cgm-text-primary" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="cgm-text-primary text-xl font-semibold">
                                                {wearable.name}
                                            </h3>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Team Devices — coaches/admins only */}
                {(userInfo?.role === 'coach' || userInfo?.role === 'admin') && (
                    <div className="mt-10" data-tour="wearable-team-devices">
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="w-5 h-5 cgm-text-primary" />
                            <h3 className="cgm-text-primary text-xl font-semibold">Team Devices</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {WEARABLE_OPTIONS.filter(w => w.available && (w.provider === 'catapult' || w.provider === 'hawkin')).map((wearable) => {
                                const logoPath = getWearableLogo(wearable);
                                return (
                                    <button
                                        key={wearable.id}
                                        onClick={() => handleWearableSelect(wearable)}
                                        className="cgm-card p-6 text-left hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] cursor-pointer relative border-2 border-transparent hover:border-opacity-20"
                                    >
                                        <div className="flex items-center space-x-5">
                                            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                                                {logoPath ? (
                                                    <img
                                                        src={logoPath}
                                                        alt={`${wearable.name} logo`}
                                                        className="w-full h-full object-contain"
                                                    />
                                                ) : (
                                                    <Watch className="w-10 h-10 cgm-text-primary" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="cgm-text-primary text-xl font-semibold">
                                                    {wearable.name}
                                                </h3>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Coming Soon Wearables */}
                <div className="mt-8">
                    <h3 className="cgm-text-secondary text-lg font-semibold mb-4 opacity-70">Coming Soon</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {WEARABLE_OPTIONS.filter(w => !w.available).map((wearable) => {
                            const logoPath = getWearableLogo(wearable);
                            return (
                                <div
                                    key={wearable.id}
                                    className="cgm-card p-6 text-left opacity-50 cursor-not-allowed"
                                >
                                    <div className="flex items-center space-x-5">
                                        <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                                            {logoPath ? (
                                                <img
                                                    src={logoPath}
                                                    alt={`${wearable.name} logo`}
                                                    className="w-full h-full object-contain opacity-60"
                                                />
                                            ) : (
                                                <Watch className="w-10 h-10 cgm-text-secondary" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="cgm-text-secondary text-lg font-semibold">
                                                    {wearable.name}
                                                </h3>
                                                <span className="text-xs cgm-text-secondary bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                                                    Coming Soon
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConnectWearable;
